/**
 * Main facet filtering form component
 * Handles all filter interactions, AJAX updates, and URL management
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Only respond to inputs within these selectors
    const inputWhitelist = [".facet", ".collection-actions-wrapper"];
    // Ignore inputs from these selectors (e.g., product compare toggle)
    const inputBlacklist = [".collection-actions-compare-toggle"];

    // Price range inputs trigger slower (500ms) to avoid excessive requests while typing
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Other filter inputs trigger faster (250ms) with immediate execution
    // The 'true' parameter makes it fire immediately on first call
    this.debouncedOnSubmitReduced = debounce(
      (event) => {
        this.onSubmitHandler(event);
      },
      250,
      true,
    );

    // Listen for any input changes (checkboxes, price inputs, sort dropdowns)
    this.addEventListener("input", (event) => {
      // Ignore inputs that aren't in whitelisted areas or are explicitly blacklisted
      if (
        !inputWhitelist.some((selector) => !!event.target.closest(selector)) ||
        inputBlacklist.some((selector) => !!event.target.closest(selector))
      ) {
        return false;
      }

      // Price range inputs get longer debounce, other inputs get shorter debounce
      if (event.target.closest("price-range")) {
        this.debouncedOnSubmit(event);
      } else {
        this.debouncedOnSubmitReduced(event);
      }
    });
  }

  /**
   * Set up browser back/forward button handling
   * Listens for popstate events to restore filter state when user navigates history
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      // Get search params from history state, or use initial page load params
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      // Avoid re-rendering if params haven't changed
      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        return;
      }

      // Re-render page with historical params, but don't update URL (it's already updated by browser)
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  /**
   * Toggle the disabled state of active filter "remove" buttons
   * Used to prevent multiple clicks while AJAX request is in progress
   */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  /**
   * Main method to render the page with new filter/sort parameters
   * @param {string} searchParams - URL query string with filter parameters
   * @param {Event} event - The triggering event (optional)
   * @param {boolean} updateURLHash - Whether to update browser URL
   * @param {boolean} appendResults - Whether to append results (for load more pagination)
   */
  static renderPage(searchParams, event, updateURLHash = true, appendResults) {
    // Ignore older responses when a newer selection replaces them.
    FacetFiltersForm.requestVersion = (FacetFiltersForm.requestVersion || 0) + 1;
    // Store current params for comparison on back/forward navigation
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");

    // Add loading state to product grid
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    // Add loading state to product count
    if (countContainer) {
      countContainer.classList.add("loading");
    }

    // The results keep their height until the new ones are in (keeps the shopper's place, see holdResults).
    FacetFiltersForm.holdResults();

    // For each section (usually just the product grid), fetch or use cached data
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      // Check if we have this data cached, otherwise fetch it
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, appendResults);
    });

    // Update browser URL with new filter params (unless navigating via back/forward)
    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    // Dispatch custom event for analytics/tracking (but not for pagination)
    if (!new URLSearchParams(searchParams).has("page")) {
      const filtersEvent = new CustomEvent("collection:product-filters", {
        detail: {
          filters: deserializeSearchParams(searchParams),
        },
      });
      document.dispatchEvent(filtersEvent);
    }
  }

  /**
   * Fetch section HTML from server via AJAX
   * @param {string} url - URL to fetch (includes section_id and filter params)
   * @param {Event} event - The triggering event
   * @param {boolean} appendResults - Whether to append results (load more pagination)
   */
  static renderSectionFromFetch(url, event, appendResults) {
    const requestVersion = FacetFiltersForm.requestVersion;
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (requestVersion !== FacetFiltersForm.requestVersion) return;
        const html = responseText;
        // Cache the response for potential back/forward navigation
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        // Update all relevant parts of the page
        FacetFiltersForm.renderCollectionActions(html);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, appendResults);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderedVersion = requestVersion;
        FacetFiltersForm.releaseResults();
      })
      .catch((error) => {
        // A failed request leaves the page as it was; it must not keep the held height either. The error still surfaces.
        if (requestVersion === FacetFiltersForm.requestVersion) {
          FacetFiltersForm.renderedVersion = requestVersion;
          FacetFiltersForm.releaseResults();
        }
        throw error;
      });
  }

  /**
   * Render section from cached HTML (used for back/forward navigation)
   * @param {Function} filterDataUrl - Function to find cached data by URL
   * @param {Event} event - The triggering event
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    // Update all relevant parts of the page from cache
    FacetFiltersForm.renderCollectionActions(html);
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderedVersion = FacetFiltersForm.requestVersion;
    FacetFiltersForm.releaseResults();
  }

  /**
   * Keep the shopper's place while results change (AJ 2026-10-11: "why when selecting or removing a filter does it
   * force scroll up again", "when i filter it makes the screen move and shift.. i dont really like that").
   * From the moment a filter or sort is chosen, the results block keeps the height it had, so the page cannot get
   * shorter under the shopper while the new results load and replace the old ones. (A page that gets shorter than the
   * place the shopper is looking at makes the browser pull the scroll position up on its own.)
   */
  static holdResults() {
    const container = document.getElementById("ProductGridContainer");
    if (!container || FacetFiltersForm.heldResults === container) return;
    container.style.minHeight = `${container.getBoundingClientRect().height}px`;
    FacetFiltersForm.heldResults = container;
  }

  /**
   * Once the newest results are in, let the held height go. The page only moves when the shopper's view would
   * otherwise end up below the new, shorter results (no result left in view, or the page now ending above the view):
   * then it glides to the top of the results (at once with reduced motion), never to the top of the page. Same or
   * longer results, or a view that still shows results, never move. While the phone filter drawer is open the hold
   * stays, so the page behind the drawer stays still; closing the drawer lets it go (drawer block at the end of this file).
   */
  static releaseResults() {
    const container = FacetFiltersForm.heldResults;
    if (!container) return;
    if (!container.isConnected) {
      FacetFiltersForm.heldResults = null;
      return;
    }
    // A newer selection is still loading, or the phone drawer covers the page: keep holding.
    if (FacetFiltersForm.renderedVersion !== FacetFiltersForm.requestVersion) return;
    if (document.body.classList.contains("page-layout-sidebar-drawer-open") && window.matchMedia("(max-width: 989px)").matches) return;
    // A glide already under way (pagination) ends first; the height is let go after it.
    if (FacetFiltersForm.gliding) {
      FacetFiltersForm.gliding.then(() => FacetFiltersForm.releaseResults());
      return;
    }

    const release = () => {
      if (FacetFiltersForm.heldResults !== container) return;
      container.style.minHeight = "";
      FacetFiltersForm.heldResults = null;
    };
    const box = container.getBoundingClientRect();
    const scrollY = window.scrollY;
    // Where the new results end. The hold keeps the box at least as tall as before, so measure what is inside it.
    let contentBottom = box.top;
    [...container.children].forEach((child) => {
      const rect = child.getBoundingClientRect();
      if (rect.height && getComputedStyle(child).position !== "absolute") contentBottom = Math.max(contentBottom, rect.bottom);
    });
    const shrink = Math.max(0, box.bottom - contentBottom - (parseFloat(getComputedStyle(container).paddingBottom) || 0));
    if (shrink < 1) {
      release();
      return;
    }
    let lastResult = box.top;
    container
      .querySelectorAll("#product-grid > *, #product-rows > *, .cq-sr-row, .search-empty-state, .cq-collection-empty__message")
      .forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.height) lastResult = Math.max(lastResult, rect.bottom);
      });
    const maxAfter = Math.max(0, document.documentElement.scrollHeight - shrink - window.innerHeight);
    let target = null;
    if (lastResult <= FacetFiltersForm.stickyOffset() + 24) {
      // No result would be left in view: go to the top of the results.
      target = FacetFiltersForm.resultsTop();
    } else if (scrollY > maxAfter + 1) {
      // Results are still in view but the page would end above the view: move only as far as needed.
      target = maxAfter;
    }
    target = target === null ? null : Math.max(0, Math.min(target, maxAfter));
    if (target === null || target >= scrollY - 1) {
      release();
      return;
    }
    // The height is let go when the glide ends, so the page never shortens under it.
    FacetFiltersForm.glideTo(target).then(release);
  }

  /** Where "the top of the results" is in the page: the toolbar above the products (or the products), under the sticky bars. */
  static resultsTop() {
    const actions = document.getElementById("CollectionActions");
    const anchor = actions?.getBoundingClientRect().height ? actions : document.getElementById("ProductGridContainer");
    if (!anchor) return window.scrollY;
    return Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - FacetFiltersForm.stickyOffset() - 16);
  }

  /**
   * Scroll the page to a position: a smooth glide, or at once when the shopper asked for reduced motion (the page's
   * own scroll-behavior is smooth, so the behavior is always set here). Resolves when the page has arrived.
   */
  static glideTo(top) {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || Math.abs(window.scrollY - top) < 1) {
      window.scrollTo({ top, behavior: "instant" });
      return Promise.resolve();
    }
    const glide = new Promise((resolve) => {
      let timer = 0;
      const finish = () => {
        clearTimeout(timer);
        window.removeEventListener("scrollend", finish);
        if (FacetFiltersForm.gliding === glide) FacetFiltersForm.gliding = null;
        resolve();
      };
      window.addEventListener("scrollend", finish);
      // Browsers without scrollend, or a glide the shopper interrupts: carry on after the longest glide.
      timer = setTimeout(finish, 1000);
      window.scrollTo({ top, behavior: "smooth" });
    });
    FacetFiltersForm.gliding = glide;
    return glide;
  }

  /** Height of the bars that stay at the top of the window while the page scrolls (header; sticky toolbar on phones). */
  static stickyOffset() {
    let offset = 0;
    document.querySelectorAll(".cqp.nav, sticky-header:not([disabled]), sticky-collection-actions .collection-actions-wrapper").forEach((el) => {
      const position = getComputedStyle(el).position;
      if (position === "sticky" || position === "fixed") offset += el.getBoundingClientRect().height;
    });
    return offset;
  }

  /**
   * Update the product grid with new products
   * @param {string} html - Full HTML response from server
   * @param {boolean} appendResults - If true, prepend existing products (load more pagination)
   */
  static renderProductGridContainer(html, appendResults) {
    const container = document.getElementById("ProductGridContainer");
    const parsedDom = new DOMParser().parseFromString(html, "text/html");

    // For "load more" pagination, keep existing products and add new ones
    if (appendResults) {
      parsedDom
        .querySelector("#ProductGridContainer #product-grid")
        .prepend(...[...document.querySelector("#ProductGridContainer #product-grid").childNodes]);
    }

    // Replace the entire container with new HTML
    container.innerHTML = parsedDom.getElementById("ProductGridContainer").innerHTML;
    const layoutSwitch = document.querySelector("product-layout-switch");
    layoutSwitch?.setLayout(layoutSwitch.layout);
  }

  /**
   * Update the product count display (e.g., "Showing 24 of 156 products")
   * @param {string} html - Full HTML response from server
   */
  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount")?.innerHTML;
    const container = document.getElementById("ProductCount");

    if (container) {
      container.innerHTML = count;
      container.classList.remove("loading");
    }
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const drawerButton = document.querySelector('.button-sidebar-facets-submit');
    const updatedButton = parsed.querySelector('.button-sidebar-facets-submit');
    if (drawerButton && updatedButton) drawerButton.innerHTML = updatedButton.innerHTML;
    if (document.body.classList.contains('page-layout-sidebar-drawer-open')) {
      const drawer = document.getElementById('CollectionFiltersDrawer');
      const focused = drawer.contains(document.activeElement) ? document.activeElement : drawer.querySelector('.page-layout-sidebar-drawer-header');
      trapFocus(drawer, focused);
    }
  }

  /**
   * Update the collection actions bar (sort dropdown, product count, active filters)
   * @param {string} html - Full HTML response from server
   */
  static renderCollectionActions(html) {
    const container = document.getElementById("CollectionActions");
    const parsedDom = new DOMParser().parseFromString(html, "text/html");

    if (!container) {
      return;
    }

    const parsedContainerActions = parsedDom.getElementById("CollectionActions");
    if (!parsedContainerActions) return;

    // Preserve sticky state if the bar is currently stuck
    if (container.classList.contains("is-stuck")) {
      parsedContainerActions.classList.add("is-stuck");
    }

    // Update classes from new HTML
    container.classList = parsedContainerActions.classList;

    // Update the main content (sort dropdown, product count)
    const parsedCollectionActionsMain = parsedContainerActions.querySelector(
      ".collection-actions-main, .section-main-search",
    );
    const collectionActionsMain = container.querySelector(".collection-actions-main");

    if (parsedCollectionActionsMain && collectionActionsMain) {
      const restoreDrawerFocus = document.activeElement?.matches('.button-sidebar-drawer-open');
      collectionActionsMain.classList = parsedCollectionActionsMain.classList;
      collectionActionsMain.innerHTML = parsedCollectionActionsMain.innerHTML;
      if (restoreDrawerFocus) collectionActionsMain.querySelector('.button-sidebar-drawer-open')?.focus({ preventScroll: true });
      const filterToggle = collectionActionsMain.querySelector(".button-sidebar-drawer-open");
      if (filterToggle) {
        filterToggle.setAttribute("aria-controls", "CollectionFiltersDrawer");
        filterToggle.setAttribute("aria-haspopup", "dialog");
        filterToggle.setAttribute("aria-expanded", document.body.classList.contains("page-layout-sidebar-drawer-open"));
      }
    }

    // Sort sits in the right-hand group (r8), outside the main block: refresh it the same way.
    const parsedSort = parsedContainerActions.querySelector(".collection-actions-sort");
    const sort = container.querySelector(".collection-actions-sort");
    if (parsedSort && sort) {
      const restoreSortFocus = Boolean(document.activeElement?.closest("collection-sort"));
      sort.innerHTML = parsedSort.innerHTML;
      if (restoreSortFocus) sort.querySelector(".cq-sort-toggle")?.focus({ preventScroll: true });
    }
  }

  /**
   * Update the filter sidebar with new facet options
   * This is the KEY method that preserves facet expanded/collapsed state
   * @param {string} html - Full HTML response from server
   * @param {Event} event - The triggering event (used to determine scroll behavior)
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const oldFacetElements = document.querySelectorAll("#FacetFiltersForm .js-filter");
    const newFacetElements = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter");

    // STEP 1: Save which facets are currently expanded BEFORE removing them from DOM
    // This preserves user's expanded state through the AJAX update
    const expandedFacets = [];
    const expandedFacetValues = [];
    document
      .querySelectorAll('#FacetFiltersForm .js-filter collapsible-expandable[expanded="true"]')
      ?.forEach((node) => {
        const filterName = node.closest(".js-filter").dataset.name;
        expandedFacets.push(filterName);
        // node is CollapsibleExpandable object
        if (!node.listCollapsed) {
          expandedFacetValues.push(filterName);
        }
      });

    // STEP 2: Save currently focused element to restore focus after update
    const facetsContainer = document.querySelector(
      "#FacetFiltersForm .page-layout-sidebar-inner-content",
    );
    const focusedElement = document.activeElement;
    let focusedFilterId = null;
    if (facetsContainer?.contains(focusedElement)) {
      focusedFilterId = focusedElement.id;
    }

    // STEP 3: Set min-height to prevent layout shift during replacement
    if (facetsContainer) {
      const previousHeight = facetsContainer?.getBoundingClientRect().height;
      facetsContainer.style.minHeight = `${previousHeight}px`;
    }

    // STEP 4: Remove old facets and add new ones, restoring expanded state
    oldFacetElements.forEach((facet) => facet.remove());
    newFacetElements.forEach((element) => {
      const filterName = element.dataset.name;

      // Append to DOM first, so that CollapsibleExpandable is initialized and usable.
      facetsContainer.append(element);

      const collapsible = element.querySelector("collapsible-expandable");
      const isExpanded = expandedFacets.includes(filterName);
      const hasExpandedValues = expandedFacetValues.includes(filterName);

      isExpanded ? collapsible.expand() : collapsible.collapse();
      hasExpandedValues ? collapsible.expandList() : collapsible.collapseList();
    });

    // STEP 5: Restore keyboard focus to the element user was interacting with
    if (focusedFilterId) {
      document.getElementById(focusedFilterId)?.focus({ preventScroll: true });
    }

    // STEP 6: Remove min-height constraint after DOM update completes
    if (facetsContainer) {
      requestAnimationFrame(() => {
        facetsContainer.style.minHeight = "";
      });
    }

    // STEP 7: Optional scroll to top behavior (if enabled in theme settings)
    // Scrolls user back to top of products when they change filters (desktop only)
    const scrollTop = document.querySelector("facet-filters-form").dataset.scrollTop === "true";
    const container = document.getElementById("CollectionActions");
    if (
      scrollTop &&
      container &&
      window.innerWidth >= 990 && // Desktop only
      !event?.target?.name?.includes(".price.") // Don't scroll for price range changes
    ) {
      const offset = document.querySelector("sticky-header:not([disabled])")?.offsetHeight ?? 0;
      const rect = container.getBoundingClientRect();
      const scrollPosition = window.scrollY + rect.top - offset;
      // Only scroll if collection actions bar is above viewport
      if (rect.top < 0) {
        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }

    // STEP 8: Update the active filters display (the chips showing selected filters)
    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  /**
   * Update the active filters display (the filter chips/tags showing current selections)
   * @param {HTMLDocument} html - Parsed HTML document from server
   */
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".collection-actions-filters", ".cq-sidebar-active-clear"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) {
        return;
      }

      // Replace the active filters HTML
      const current = document.querySelector(selector);
      if (current) current.innerHTML = activeFacetsElement.innerHTML;
    });

    // Re-enable the remove buttons (they were disabled at the start of the request)
    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Update the browser URL with filter parameters
   * @param {string} searchParams - URL query string to set
   * @param {boolean} replace - If true, replace current history entry instead of pushing new one
   */
  static updateURLHash(searchParams, replace) {
    if (replace) {
      // Replace current URL without adding to history (used for page load initialization)
      history.replaceState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
      );
    } else {
      // Push new URL to history (allows back/forward navigation)
      history.pushState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
      );
    }
  }

  /**
   * Get the sections that need to be fetched via AJAX
   * @returns {Array} Array of section objects with section IDs
   */
  static getSections() {
    return [
      {
        section: document.querySelector("#ProductGridContainer #product-grid, #ProductGridContainer #product-rows").dataset.id,
      },
    ];
  }

  /**
   * Handle form submission (triggered by debounced input events)
   * @param {Event} event - The form submit event
   */
  onSubmitHandler(event) {
    event.preventDefault();
    // A delayed price-input event that lands after the phone drawer closed is skipped: closing applied the form already.
    if (event.target.closest('#CollectionFiltersDrawer') && window.matchMedia('(max-width: 989px)').matches && !document.body.classList.contains('page-layout-sidebar-drawer-open')) return;
    // Collect all form data (checked filters, sort option, price range)
    const formData = new FormData(document.getElementById("FacetsFilterForm"));
    const searchParams = new URLSearchParams(formData).toString();
    // Trigger page re-render with new parameters
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * Handle clicks on active filter remove buttons (the X on filter chips)
   * @param {Event} event - The click event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    // Disable all remove buttons to prevent multiple clicks
    FacetFiltersForm.toggleActiveFacets();

    // Extract search params from the removal link's href
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);

    // Re-render with the filter removed
    FacetFiltersForm.renderPage(url);
  }

  /**
   * Handle AJAX pagination (clicking page numbers)
   * @param {Event} event - The click event on pagination link
   */
  onAjaxPagination(event) {
    event.preventDefault();
    const next = event.currentTarget;

    // Extract search params from pagination link
    const url = next.href.indexOf("?") == -1 ? "" : next.href.slice(next.href.indexOf("?") + 1);

    // Render new page
    FacetFiltersForm.renderPage(url, event, true);

    // Go to the top of the results for the new page (at once with reduced motion); the old results keep their height
    // meanwhile, so the glide has room to finish.
    FacetFiltersForm.glideTo(FacetFiltersForm.resultsTop());

    // Dispatch custom event for analytics/tracking
    const params = new URLSearchParams(url);
    const customEvent = new CustomEvent("pagination:page-change", {
      detail: {
        page: Number(params.get("page")),
      },
    });
    document.dispatchEvent(customEvent);
  }
}

// Initialize static properties for caching and history management
FacetFiltersForm.filterData = []; // Cache of fetched HTML responses
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1); // Initial page load params
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1); // Previous params for comparison

// Register the custom element
customElements.define("facet-filters-form", FacetFiltersForm);

// Set up browser back/forward button listeners
FacetFiltersForm.setListeners();

/**
 * Price range filter component
 * Handles the min/max price input fields and ensures valid ranges
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.priceInputs = this.querySelectorAll('input[type="number"]');
    this.sliders = this.querySelectorAll('input[type="range"]');
    this.sliderTrack = this.querySelector(".cq-facet-sliders");
    this.rangeMax = Number(this.priceInputs[1].max);
    try {
      this.formatter = new Intl.NumberFormat(document.documentElement.lang, {
        style: "currency",
        currency: this.dataset.currency,
      });
    } catch {
      this.formatter = new Intl.NumberFormat();
    }

    this.priceInputs.forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("input", () => this.syncSliders());
    });
    this.sliders.forEach((slider, index) => {
      slider.max = this.rangeMax;
      slider.addEventListener("input", (event) => {
        // Keep the existing AJAX renderer from replacing a thumb during a drag.
        event.stopPropagation();
        if (this.stops && !this.stepScale) slider.value = this.snap(Number(slider.value));
        this.updateFromSlider(index);
      });
      slider.addEventListener("change", () => {
        this.updateFromSlider(index);
        this.priceInputs[index].dispatchEvent(new Event("input", { bubbles: true }));
      });
      slider.addEventListener("keydown", (event) => this.onStopKey(event, index));
    });
    this.setMinAndMaxValues();
    this.syncSliders();
    if (this.sliderTrack && this.rangeMax > 0) this.sliderTrack.hidden = false;
    // Lane H (AJ 2026-10-10 21:54, "price slider snaps to real product prices"): on a collection page the handles stop at
    // the collection's product prices (each product's lowest price, the "From" on its card, read by
    // cq-price-histogram.js), plus 0 and the top of the range, instead of every cent. Arrow keys move one stop, Page Up
    // and Page Down three, Home and End go to the ends. A price typed in the boxes is kept as typed.
    this.addEventListener("cq:price-stops", () => this.setStops(this.cqStops));
    if (this.cqStops) this.setStops(this.cqStops);
  }

  setStops(list) {
    const max = this.rangeMax;
    const prices = (list || []).map((n) => Math.round(n * 100) / 100).filter((n) => n > 0 && n < max);
    const stops = [...new Set([0, ...prices, max])].sort((a, b) => a - b);
    this.stops = max > 0 && stops.length > 2 ? stops : null;
    // Lane X (AJ 2026-10-11 05:48, "why is price slider not working in search?"): search results run from a few dollars to
    // commercial systems (q=filter tops out near $66,000), so on a straight dollar scale everything under $3,000 sat in
    // the first few pixels and a drag moved past almost every product. On the search page (data-cq-search-stops) the
    // track is laid out by stop instead: every product price gets the same width, and the boxes still take any price.
    this.stepScale = Boolean(this.stops) && this.hasAttribute("data-cq-search-stops");
    if (this.stepScale) {
      this.sliders.forEach((slider) => {
        slider.min = 0;
        slider.max = this.stops.length - 1;
        slider.step = 1;
      });
      this.syncSliders();
    }
  }

  snap(value) {
    return this.stops.reduce((best, stop) => (Math.abs(stop - value) < Math.abs(best - value) ? stop : best), this.stops[0]);
  }

  onStopKey(event, index) {
    if (!this.stops) return;
    const moves = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageDown: -3, PageUp: 3, Home: -Infinity, End: Infinity };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const slider = this.sliders[index];
    const at = this.stepScale ? Number(slider.value) : this.stops.indexOf(this.snap(Number(slider.value)));
    const next = Math.max(0, Math.min(this.stops.length - 1, at + moves[event.key]));
    if (next === at) return;
    slider.value = this.stepScale ? next : this.stops[next];
    this.updateFromSlider(index);
    this.priceInputs[index].dispatchEvent(new Event("input", { bubbles: true }));
  }

  /**
   * Handle changes to price inputs
   * @param {Event} event - The input change event
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSliders();
  }

  updateFromSlider(index) {
    const low = Number(this.priceInputs[0].value || 0);
    const high = Number(this.priceInputs[1].value || this.rangeMax);
    const value = this.stepScale ? this.stops[Number(this.sliders[index].value)] : Number(this.sliders[index].value);
    this.priceInputs[index].value = index === 0 ? Math.min(value, high) : Math.max(value, low);
    this.setMinAndMaxValues();
    this.syncSliders();
  }

  syncSliders() {
    if (!this.sliderTrack || this.sliders.length !== 2) return;
    const low = Math.max(0, Math.min(Number(this.priceInputs[0].value || 0), this.rangeMax));
    const high = Math.max(low, Math.min(Number(this.priceInputs[1].value || this.rangeMax), this.rangeMax));
    if (this.stepScale) {
      // Each handle sits on the stop at or just outside the chosen price, so the shaded track covers it.
      const last = this.stops.length - 1;
      let lo = 0;
      while (lo < last && this.stops[lo + 1] <= low) lo++;
      let hi = last;
      while (hi > lo && this.stops[hi - 1] >= high) hi--;
      this.sliders[0].value = lo;
      this.sliders[1].value = hi;
      this.sliders[0].setAttribute("aria-valuemax", hi);
      this.sliders[1].setAttribute("aria-valuemin", lo);
      this.sliders[0].setAttribute("aria-valuetext", this.formatter.format(low));
      this.sliders[1].setAttribute("aria-valuetext", this.formatter.format(high));
      this.sliderTrack.style.setProperty("--cq-price-start", `${(lo / last) * 100}%`);
      this.sliderTrack.style.setProperty("--cq-price-end", `${(hi / last) * 100}%`);
      return;
    }
    this.sliders[0].value = low;
    this.sliders[1].value = high;
    this.sliders[0].setAttribute("aria-valuemax", high);
    this.sliders[1].setAttribute("aria-valuemin", low);
    this.sliders[0].setAttribute("aria-valuetext", this.formatter.format(low));
    this.sliders[1].setAttribute("aria-valuetext", this.formatter.format(high));
    this.sliderTrack.style.setProperty("--cq-price-start", `${this.rangeMax ? low / this.rangeMax * 100 : 0}%`);
    this.sliderTrack.style.setProperty("--cq-price-end", `${this.rangeMax ? high / this.rangeMax * 100 : 100}%`);
  }

  /**
   * Update min/max constraints on both inputs to prevent invalid ranges
   * E.g., if max is set to 100, min can't be more than 100
   */
  setMinAndMaxValues() {
    const inputs = this.priceInputs;
    const minInput = inputs[0];
    const maxInput = inputs[1];

    // Max input value becomes the ceiling for min input
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    // Min input value becomes the floor for max input
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    // Reset to defaults if cleared
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "") minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  /**
   * Clamp input value to valid range
   * @param {HTMLInputElement} input - The input to validate
   */
  adjustToValidValues(input) {
    if (input.value === "") return;
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

/**
 * Facet remove button component
 * Wraps individual filter removal links (X buttons on filter chips)
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    // When clicked, trigger the parent form's filter removal handler
    this.querySelector("a").addEventListener("click", (event) => {
      event.preventDefault();
      const form =
        this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define("facet-remove", FacetRemove);

/**
 * AJAX pagination component
 * Handles pagination links to load new pages without full page refresh
 */
class AjaxPaginate extends HTMLElement {
  constructor() {
    super();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    const buttons = this.querySelectorAll("a");

    // Make all pagination links trigger AJAX instead of full page load
    buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        form.onAjaxPagination(event);
      });
    });
  }
}

customElements.define("ajax-paginate", AjaxPaginate);

/**
 * Sticky collection actions bar component
 * Makes the filter/sort bar stick to top of page when scrolling
 */
class StickyCollectionActions extends HTMLElement {
  constructor() {
    super();

    this.sticky = this.children[0];
    this.createObserver();
  }

  /**
   * Use IntersectionObserver to detect when bar should be stuck
   */
  createObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Add 'is-stuck' class when the element scrolls above viewport
        entry.target.classList.toggle("is-stuck", entry.boundingClientRect.top < 0);
      },
      { threshold: 1 },
    );

    observer.observe(this.sticky);
  }
}

customElements.define("sticky-collection-actions", StickyCollectionActions);

/**
 * Utility function: Convert URL query string to object
 * Handles multiple values for the same key by joining them with commas
 *
 * Example input: "filter.v.option.color=Blue&filter.v.option.color=Red&sort_by=price-asc"
 * Example output: { "filter.v.option.color": "Blue,Red", "sort_by": "price-asc" }
 *
 * @param {string} queryString - The query string to parse
 * @returns {Object} Deserialized parameters object
 */
const deserializeSearchParams = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};

  for (const [key, value] of params.entries()) {
    // If key already exists, append the new value with a comma
    if (result[key]) {
      result[key] = `${result[key]},${value}`;
    } else if (value) {
      result[key] = value;
    }
  }

  return result;
};

// Desktop sidebar toggle (r8 items 12 and 14). The button reads "Hide filters" while the sidebar is open and
// "Show filters" while it is closed (both labels are in the markup; html.cq-filters-hidden picks one before
// first paint). The choice is remembered per visitor. When the browser supports View Transitions and the
// visitor has not asked for reduced motion, the change animates for 260 ms (see nameParticipants and the
// item 14 rules in component-facets.css); the real layout still changes in one step, so the last frame is
// the page itself. Otherwise the layout changes at once, as before.
if (!customElements.get('filters-toggle')) customElements.define('filters-toggle', class extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.button = this.querySelector('button');
    this.wrap = this.closest('.facets-wrap');
    this.sidebar = this.wrap?.querySelector('.page-layout-sidebar');
    if (!this.button || !this.sidebar) return;
    let collapsed = false;
    try { collapsed = localStorage.getItem('collectionSidebarCollapsed') === 'collapsed'; } catch (_) {}
    this.collapsed = collapsed;
    this.apply(collapsed);
    this.button.addEventListener('click', () => this.toggle());
  }
  toggle() {
    // The wanted state is tracked here, not read from the page, so two toggles in quick succession always
    // end where they started, even if the first update has not run yet. (Clicks that land while the
    // animation plays go to the page, not the button: browsers make the page inert for those 260 ms.)
    const collapse = !this.collapsed;
    this.collapsed = collapse;
    try { localStorage.setItem('collectionSidebarCollapsed', collapse ? 'collapsed' : 'expanded'); } catch (_) {}
    this.transition?.skipTransition();
    // The motion relies on view-transition-class (Chrome 125 and Safari 18.2 onward); a browser with
    // View Transitions but without classes would draw the default crossfade, so it gets the instant change.
    const animate = typeof document.startViewTransition === 'function'
      && window.CSS?.supports?.('view-transition-class', 'cq-vt-card')
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && window.matchMedia('(min-width: 990px)').matches;
    if (!animate) { this.apply(collapse); return; }
    const token = (this.token || 0) + 1;
    this.token = token;
    const root = document.documentElement;
    this.clearNames();
    root.style.setProperty('--cq-vt-bg', this.pageBackground());
    root.classList.add('cq-filters-vt');
    this.nameParticipants(collapse);
    // Safety net: the page ignores clicks while a View Transition runs, so one that has not finished well
    // after its 260 ms (seen once in a test browser under load) is skipped, which jumps to the end state.
    // Animations someone has paused on purpose (browser devtools, frame-by-frame checks) are left alone.
    const guard = setTimeout(() => {
      const paused = document.getAnimations?.().some(a => a.playState === 'paused' && a.effect?.pseudoElement?.startsWith('::view-transition'));
      if (this.token === token && !paused) this.transition?.skipTransition();
    }, 1000);
    const done = () => {
      clearTimeout(guard);
      if (this.token !== token) return;
      this.clearNames();
      root.classList.remove('cq-filters-vt');
      root.style.removeProperty('--cq-vt-bg');
      this.transition = null;
    };
    try {
      this.transition = document.startViewTransition(() => {
        if (this.token === token) this.nameArrivals();
        this.apply(this.collapsed);
      });
      this.transition.finished.then(done, done);
    } catch (_) {
      done();
      this.apply(collapse);
    }
  }
  clearNames() {
    (this.named || []).forEach(el => { el.style.removeProperty('view-transition-name'); el.style.removeProperty('view-transition-class'); });
    (this.restore || []).forEach(([el, prop, value, priority]) => {
      if (value) el.style.setProperty(prop, value, priority); else el.style.removeProperty(prop);
    });
    this.named = [];
    this.restore = [];
    this.arrivals = [];
  }
  // The colour behind the grid. Cards are painted with it while they move (component-facets.css), so a card
  // passing over another covers it instead of mixing their words.
  pageBackground() {
    for (let el = this.wrap; el; el = el.parentElement) {
      const color = getComputedStyle(el).backgroundColor;
      if (color && color !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(color)) return color;
    }
    return '#fff';
  }
  // Name each piece that moves so the browser animates it on its own layer, in this paint order (lowest first):
  //   1. the filter column: slides 24 px toward the page edge and fades, out in the first 100 ms when
  //      hiding, in from 117 ms when showing (after the cards have moved off it);
  //   2. cards that change rows (the grid goes from 3 to 4 columns and back) or would travel more than one
  //      and a half columns: they fade out where they were in the first 120 ms and fade in where they land
  //      from 100 ms, on curves that keep both faint at the handover ("cq-vt-out" names are swapped for
  //      "cq-vt-in" names inside the update, see nameArrivals), so two copies of a card are never on screen
  //      together;
  //   3. the other cards (same row, a short move) glide and resize. The card box carries only its
  //      background; its picture scales with the box, while its words (name, price, rating) are separate
  //      pieces that slide at their real size, so text is never stretched or doubled and the first frame
  //      matches the page before the click. Words whose size or line breaks change fade out and in during
  //      the move. The toggle, the active filter links and the pagination glide with them;
  //   4. fixed layers (compare tray, cookie banner, chat, pop-ups) stay on top, unchanged.
  // Only cards near the viewport (before or after the change) take part; the rest change unseen.
  nameParticipants(collapse) {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const near = rect => rect.width > 0 && rect.right > 0 && rect.left < vw && rect.bottom > -vh * 0.5 && rect.top < vh * 1.5;
    // Everything is read first and named at the end, so the page is laid out only for the two measurements.
    const plan = [];
    const name = (el, value, group) => { if (el) plan.push([el, value, group]); };
    name(this.sidebar, 'cq-vt-filters');
    name(this.button, 'cq-vt-toggle');
    const main = this.wrap.querySelector('.page-layout-main-content');
    const chips = main?.querySelector('.collection-actions-filters');
    if (chips?.children.length) name(chips, 'cq-vt-chips', 'cq-vt-static');
    const pages = main?.querySelector('.pagination');
    if (pages && near(pages.getBoundingClientRect())) name(pages, 'cq-vt-pages', 'cq-vt-center');
    const cards = [...(main?.querySelectorAll('#ProductGridContainer :is(#product-grid, #product-rows) > *') || [])]
      .filter(card => !card.closest('[hidden]'));
    // Where a card's name and price break into lines: each line's offset and width (so a change of font
    // size shows too). Words can only be blended when this is the same before and after.
    const breaks = card => {
      const heading = card.querySelector('.cq-card-heading, .row-heading');
      if (!heading) return '';
      const top = heading.getBoundingClientRect().top;
      const lines = new Map();
      const range = document.createRange();
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!node.textContent.trim()) continue;
        range.selectNodeContents(node);
        for (const rect of range.getClientRects()) {
          if (rect.width < 2) continue;
          const y = Math.round(rect.top - top);
          lines.set(y, (lines.get(y) || 0) + Math.round(rect.width));
        }
      }
      return [...lines].map(([y, width]) => `${y}:${width}`).join(' ');
    };
    const measure = () => {
      const rects = cards.map(card => card.getBoundingClientRect());
      const tops = rects.map(rect => Math.round(rect.top));
      const lines = [...new Set(tops)].sort((x, y) => x - y);
      return { rects, words: cards.map(breaks), rows: tops.map(top => lines.indexOf(top)) };
    };
    // A card's pieces: its picture (scaled with the card) and its words (real size). Each must be one box
    // with a size (a piece is drawn inside its own box): an inline or empty wrapper, such as the compare
    // checkbox element in the rows view (0 px wide, its box overflowing), is replaced by its children.
    const boxes = el => {
      const display = getComputedStyle(el).display;
      if (display === 'none') return [];
      const rect = el.getBoundingClientRect();
      return display === 'inline' || display === 'contents' || !rect.width || !rect.height ? [...el.children].flatMap(boxes) : [el];
    };
    // A piece inside a positioned box (the rows view compare checkbox sits over the start of the name) is
    // drawn above the plain words, as on the page.
    const raised = (el, card) => {
      for (let node = el; node && node !== card; node = node.parentElement) if (getComputedStyle(node).position !== 'static') return true;
      return false;
    };
    const pieces = card => [
      ...[...card.querySelectorAll('.cq-card-media')].flatMap(boxes).map(el => [el, 'cq-vt-media']),
      ...[...card.querySelectorAll('.cq-card-name, .cq-card-price, .cq-card-reviews, .row-content > *, .product-row > .row-actions')].flatMap(boxes)
        .map(el => [el, raised(el, card) ? 'cq-vt-text cq-vt-over' : 'cq-vt-text']),
    ];
    const layers = this.findLayers(plan.map(([el]) => el));
    // Measure where every card will land: apply the new layout, read it, put the current one back. All of
    // this happens before the next frame, so nothing is painted in between.
    const current = !this.wrap.classList.contains('page-layout-with-sidebar');
    const before = measure();
    const parts = cards.map((card, index) => (near(before.rects[index]) ? pieces(card) : []));
    this.layout(collapse);
    const after = measure();
    this.layout(current);
    cards.forEach((card, index) => {
      const from = before.rects[index];
      const to = after.rects[index];
      if (!near(from) && !near(to)) return;
      const glide = near(from)
        && before.rows[index] === after.rows[index]
        && Math.abs(to.left - from.left) < 1.5 * Math.min(from.width, to.width);
      if (glide) {
        // Words that change size or line breaks at the new width (the card name steps up a size on wider
        // cards) cannot be blended without showing twice: they fade out and back in instead.
        const rewrap = before.words[index] !== after.words[index];
        name(card, `cq-vt-card-${index + 1}`, 'cq-vt-card');
        parts[index].forEach(([el, group], n) => name(el, `cq-vt-card-${index + 1}-${n}`, rewrap && group.startsWith('cq-vt-text') ? `${group} cq-vt-retext` : group));
      } else {
        name(card, `cq-vt-out-${index + 1}`, 'cq-vt-out');
        this.arrivals.push([card, `cq-vt-in-${index + 1}`]);
      }
    });
    layers.forEach(([el, cover], index) => {
      if (cover) this.stretch(el);
      name(el, `cq-vt-layer-${index + 1}`, cover ? 'cq-vt-layer cq-vt-cover' : 'cq-vt-layer');
    });
    plan.forEach(([el, value, group]) => {
      el.style.setProperty('view-transition-name', value);
      if (group) el.style.setProperty('view-transition-class', group);
      this.named.push(el);
    });
  }
  // Runs inside the update, after the old picture is taken: a card that changes rows leaves under one name
  // and arrives under another, so its two pictures fade separately and never travel across the grid.
  nameArrivals() {
    (this.arrivals || []).forEach(([card, value]) => {
      card.style.setProperty('view-transition-name', value);
      card.style.setProperty('view-transition-class', 'cq-vt-in');
    });
  }
  // Fixed layers anywhere in the page (not only direct children of body): the compare tray is a fixed div
  // inside a static <cq-compare-tray>, chat and pop-ups are fixed boxes. A name set inside a shadow root does
  // not take part in a page transition, so a fixed host whose visible layer lives in its open shadow root
  // (the Pandectes cookie banner: a fixed <dialog> in <pandectes-cmp>, whose host box is 0 x 0) is lifted
  // instead, with its box stretched over the viewport for the 260 ms so its picture includes that layer
  // (clipped to that box, so a shadow never spills over the page scrollbar). Returns [element, stretch].
  findLayers(taken) {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const shown = (el, style) => {
      if (style.visibility === 'hidden') return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < vw && rect.bottom > 0 && rect.top < vh;
    };
    const shadowLayer = root => [...root.querySelectorAll('*')].some(el => {
      const style = getComputedStyle(el);
      return (style.position === 'fixed' && shown(el, style)) || (el.shadowRoot && shadowLayer(el.shadowRoot));
    });
    const skip = new Set([this.wrap.querySelector('#ProductGridContainer'), ...taken]);
    const layers = [];
    const visit = parent => {
      for (let el = parent.firstElementChild; el; el = el.nextElementSibling) {
        if (skip.has(el)) continue;
        const style = getComputedStyle(el);
        if (style.display === 'none') continue;
        if (style.position === 'fixed' && !el.contains(this.wrap)) {
          if (shown(el, style)) { layers.push([el, false]); continue; }
          if (el.shadowRoot && shadowLayer(el.shadowRoot)) { layers.push([el, true]); continue; }
        }
        visit(el);
      }
    };
    visit(document.body);
    return layers;
  }
  stretch(el) {
    ['top', 'right', 'bottom', 'left', 'width', 'height'].forEach(prop => {
      this.restore.push([el, prop, el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)]);
      el.style.setProperty(prop, /^(width|height)$/.test(prop) ? 'auto' : '0', 'important');
    });
  }
  layout(collapsed) {
    this.wrap.classList.toggle('page-layout-with-sidebar', !collapsed);
    this.sidebar.classList.toggle('is-collapsed', collapsed);
    document.documentElement.classList.toggle('cq-filters-hidden', collapsed);
  }
  apply(collapsed) {
    this.layout(collapsed);
    this.button.setAttribute('aria-expanded', String(!collapsed));
  }
});

if (!customElements.get('product-layout-switch')) customElements.define('product-layout-switch', class extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.buttons = [...this.querySelectorAll('button')];
    let layout = document.querySelector('#ProductGridContainer')?.dataset.defaultLayout || 'grid';
    try { layout = localStorage.getItem('product-layout') || layout; } catch (_) {}
    this.setLayout(layout);
    this.addEventListener('click', event => {
      const button = event.target.closest('button[data-layout]');
      if (!button) return;
      this.setLayout(button.dataset.layout);
      try { localStorage.setItem('product-layout', this.layout); } catch (_) {}
    });
  }
  setLayout(layout) {
    this.layout = ['grid', 'card_rows', 'rows'].includes(layout) ? layout : 'grid';
    ['grid', 'card_rows', 'rows'].forEach(value => document.documentElement.classList.toggle(`cq-layout-${value}`, value === this.layout));
    const container = document.querySelector('#ProductGridContainer');
    if (!container) return;
    const grid = container.querySelector('#product-grid');
    const rows = container.querySelector('#product-rows');
    const gridWrap = grid?.closest('.collection');
    const rowsWrap = rows?.closest('.collection');
    container.dataset.cqLayout = this.layout;
    this.buttons.forEach(button => {
      button.classList.toggle('is-active', button.dataset.layout === this.layout);
      button.setAttribute('aria-pressed', String(button.dataset.layout === this.layout));
    });
    if (gridWrap) gridWrap.hidden = Boolean(this.layout === 'rows' && rows);
    if (rowsWrap) rowsWrap.hidden = this.layout !== 'rows';
    grid?.classList.toggle('product-grid-row', this.layout === 'card_rows' || this.layout === 'rows' && !rows);
  }
});

// The select stays in Shopify's GET form; the menu only changes its value.
class CollectionSort extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.toggle = this.querySelector('.cq-sort-toggle');
    this.menu = this.querySelector('[role="menu"]');
    this.select = this.querySelector('select');
    this.items = [...this.querySelectorAll('[role="menuitemradio"]')];
    this.toggle.hidden = false;
    this.select.hidden = true;
    this.toggle.addEventListener('click', () => this.setOpen(this.menu.hidden), options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.menu.hidden) {
        event.preventDefault(); event.stopPropagation(); this.setOpen(false, true);
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (this.menu.hidden) { this.setOpen(true); return; }
        let index = this.items.indexOf(document.activeElement);
        index = event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + this.items.length) % this.items.length;
        this.items[index].focus();
      } else if (event.key === 'Tab') this.setOpen(false);
    }, options);
    this.menu.addEventListener('click', event => {
      const item = event.target.closest('[data-value]');
      if (!item) return;
      this.select.value = item.dataset.value;
      this.items.forEach(button => button.setAttribute('aria-checked', String(button === item)));
      this.setOpen(false, true);
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
    }, options);
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) this.setOpen(false);
    }, options);
  }
  setOpen(open, restoreFocus = false) {
    this.menu.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      this.place();
      // preventScroll: a plain focus() made the browser scroll (smoothly, the page's scroll-behavior) to centre the
      // option whenever the menu was not fully in view, e.g. from 1381 px to the top of the search page.
      (this.items.find(item => item.getAttribute('aria-checked') === 'true') || this.items[0])?.focus({ preventScroll: true });
    } else if (restoreFocus) this.toggle.focus({ preventScroll: true });
  }
  // The menu opens below Sort. When the window has no room for it there but has room above Sort, it opens upward, so
  // the page stays still. Only when it fits neither way does the page move, by the least amount that shows it.
  place() {
    this.menu.classList.remove('cq-sort-menu--up');
    const top = FacetFiltersForm.stickyOffset();
    const bottom = window.innerHeight - 8;
    let menu = this.menu.getBoundingClientRect();
    if (menu.top >= top && menu.bottom <= bottom) return;
    if (menu.bottom > bottom && this.toggle.getBoundingClientRect().top - 8 - menu.height >= top) {
      this.menu.classList.add('cq-sort-menu--up');
      return;
    }
    menu = this.menu.getBoundingClientRect();
    const by = menu.top < top ? menu.top - top : Math.min(menu.bottom - bottom, menu.top - top);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: by, behavior: reduce ? 'instant' : 'smooth' });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
customElements.define('collection-sort', CollectionSort);

// Selections preview live while the drawer is open. Fix K (AJ 2026-10-11 02:37): closing it any way (the X, Escape,
// the backdrop) applies the ticked selection exactly like the Show products button; it used to put the entry state
// back, so a filter ticked and closed with X was lost. Keep the existing theme drawer and its focus-return contract.
(() => {
  const drawer = document.querySelector('.cq-catalog #CollectionFiltersDrawer');
  if (!drawer) return;
  let wasOpen = false;
  let openParams = '';
  let openPrev = '';
  const formParams = () => new URLSearchParams(new FormData(document.getElementById('FacetsFilterForm'))).toString();
  const apply = () => {
    const params = formParams();
    if (params !== FacetFiltersForm.searchParamsPrev) FacetFiltersForm.renderPage(params);
  };
  document.addEventListener('click', event => {
    if (event.target.closest('.button-sidebar-facets-submit')) apply();
  }, true);
  const observer = new MutationObserver(() => {
    const open = document.body.classList.contains('page-layout-sidebar-drawer-open');
    if (open === wasOpen) return;
    wasOpen = open;
    if (open) {
      // The form's own query can be spelled differently from the page URL (empty price fields), so a drawer opened and
      // closed with no change is recognised by the form and the last rendered query staying as they were.
      openParams = formParams();
      openPrev = FacetFiltersForm.searchParamsPrev;
      // Lane H2: the drawer keeps the sidebar's groups as they are (Price and the first useful group open, the rest
      // collapsed, collection-filters.liquid; then whatever the shopper opened or closed) and only opens a group with a
      // selection. It used to collapse every group without one, Price included, as the drawer opened.
      drawer.querySelectorAll('collapsible-expandable').forEach(group => {
        const facet = group.closest('.js-filter');
        const active = facet.querySelector('input:checked') || [...facet.querySelectorAll('input[type="number"]')].some(input => input.value);
        if (active) group.expand();
      });
      trapFocus(drawer, drawer.querySelector('.button-sidebar-drawer-dismiss'));
    } else {
      if (formParams() !== openParams || FacetFiltersForm.searchParamsPrev !== openPrev) apply();
      // The page behind the drawer kept its height while the drawer was open (releaseResults); let it go now. When a
      // new request is still loading, it lets go itself once its results are in.
      FacetFiltersForm.releaseResults();
    }
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
})();
